// src/components/FittingShopQrUpload.jsx
import React, { useState } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { db } from "../firebase/firebaseConfig";

const FittingShopQrUpload = () => {
  const [productCode, setProductCode] = useState("");
  const [description, setDescription] = useState("");
  const [customerCode, setCustomerCode] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const resetForm = () => {
    setProductCode("");
    setDescription("");
    setCustomerCode("");
    setCustomerName("");
    setFile(null);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMsg("");

    if (!productCode.trim() || !description.trim() || !customerCode.trim() || !customerName.trim()) {
      setError("Fill in all fields before saving.");
      return;
    }
    if (!file) {
      setError("Select a QR / barcode file to upload.");
      return;
    }

    try {
      setBusy(true);

      const auth = getAuth();
      const user = auth.currentUser;
      const uid = user?.uid || null;
      const email = user?.email || null;

      const functions = getFunctions(undefined, "europe-west2");

    // 1) Ensure FittingShop/QR Codes/{customerKey} folder exists
    const ensureFolder = httpsCallable(functions, "spEnsureFolder");

    const customerKey =
    (customerCode || "").trim().toUpperCase() +
    (customerName.trim() ? ` - ${customerName.trim()}` : "");

    const { data: folderData } = await ensureFolder({
    type: "fittingShopQr",
    subId: customerKey,
    });


      const folderItemId = folderData.folderItemId;
      const folderWebUrl = folderData.folderWebUrl;

      if (!folderItemId) {
        throw new Error("SharePoint folderItemId missing from response.");
      }

      // 2) Create an upload session in that folder
      const createSession = httpsCallable(functions, "spCreateUploadSession");
      const { data: sessionData } = await createSession({
        folderItemId,
        fileName: file.name,
      });

      const uploadUrl = sessionData.uploadUrl;
      if (!uploadUrl) throw new Error("No uploadUrl returned from spCreateUploadSession.");

      // 3) Upload the file bytes directly to Microsoft Graph
      const buffer = await file.arrayBuffer();
      const bytes = buffer.byteLength;

      const uploadResp = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Length": String(bytes),
          "Content-Range": `bytes 0-${bytes - 1}/${bytes}`,
          "Content-Type": file.type || "application/octet-stream",
        },
        body: buffer,
      });

      if (!uploadResp.ok) {
        const txt = await uploadResp.text().catch(() => "");
        throw new Error(`Upload failed: ${uploadResp.status} ${txt}`);
      }

      const item = await uploadResp.json(); // driveItem
      const fileItemId = item.id;
      const fileName = item.name;
      const fileWebUrl = item.webUrl;
      const downloadUrl = item["@microsoft.graph.downloadUrl"] || null;

      // 4) Store record in Firestore
      await addDoc(collection(db, "fittingshop_qr"), {
        productCode: productCode.trim(),
        description: description.trim(),
        customerCode: customerCode.trim(),
        customerName: customerName.trim(),
        sp: {
          folderItemId,
          folderWebUrl,
          fileItemId,
          fileName,
          fileWebUrl,
          downloadUrl,
        },
        createdAt: serverTimestamp(),
        createdBy: { uid, email },
      });

      resetForm();
      setSuccessMsg("Saved.");
    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to upload QR / barcode.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-6 max-w-3xl mx-auto bg-white rounded-xl shadow-lg border border-gray-200">
      {/* header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <h2 className="text-base font-semibold tracking-wide">
          FittingShop – Add QR / Barcode
        </h2>
        {busy && (
          <span className="text-xs text-gray-500">
            Uploading…
          </span>
        )}
      </div>

      {/* body */}
      <form onSubmit={handleSubmit} className="px-4 py-4 space-y-4">
        {/* feedback */}
        {(error || successMsg) && (
          <div className="text-sm">
            {error && (
              <p className="text-red-600 font-medium">
                {error}
              </p>
            )}
            {!error && successMsg && (
              <p className="text-green-700">
                {successMsg}
              </p>
            )}
          </div>
        )}

        {/* inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">
              Product Code
            </label>
            <input
              type="text"
              value={productCode}
              onChange={(e) => setProductCode(e.target.value.toUpperCase())}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
              placeholder="TSMI150/30/HP/SL"
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              placeholder="150/30 Heat Pump SL"
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">
              Customer Code
            </label>
            <input
              type="text"
              value={customerCode}
              onChange={(e) => setCustomerCode(e.target.value.toUpperCase())}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm font-mono"
              placeholder="YOW"
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">
              Customer Name
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
              placeholder="Midsummer"
              autoComplete="off"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-1">
            QR / Barcode File
          </label>
          <input
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            PDF or image (PNG/JPG/SVG). Stored in SharePoint under{" "}
                <span className="font-mono">
                    FittingShop/QR Codes/{customerCode || "{CustomerCode}"} - {customerName || "{CustomerName}"}
                </span>.
         </p>
        </div>

        <div className="pt-2 flex justify-end">
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-1.5 text-sm font-semibold rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FittingShopQrUpload;